/**
 * Escrow deposits — the amount a tenant puts behind a deployment.
 *
 * A deployment's escrow account pays its lease block by block and the lease
 * closes when it runs dry, so the deposit is the tenant's budget, not a fee:
 * whatever is left is refunded on close. The console used to fix the first
 * deposit at the chain minimum and offer no way to add to it, which meant every
 * instance started with the smallest runway the chain allows and could only be
 * extended by closing it and renting again.
 *
 * Amounts are typed in dollars and sent in `uddt`, which the tenant chain
 * defines as exactly one microdollar (see `core/pricing.ts`).
 */

import { toMicroAmount } from './fees'
import { BLOCK_SECONDS, usdMicrosPerHour, type PriceCoin } from './pricing'

/**
 * The chain's minimum FIRST deposit: `x/deployment`'s
 * `DefaultParams().MinDeposits` is exactly `500000uddt`
 * (`pkg.akt.dev/go@v0.1.8/node/deployment/v1beta4/params.go`). A chain running
 * non-default params can require more, which is why the copy says "at least".
 */
export const MIN_FIRST_DEPOSIT_MICRO = 500_000n

/**
 * A top-up has no minimum beyond "more than zero": `MsgAccountDeposit`
 * rejects only a zero amount (`escrow/v1/msgs.go` ValidateBasic), and the
 * escrow handler adds it to the account as given.
 */
export const MIN_TOP_UP_MICRO = 1n

/** Dollar presets offered beside the field. The first is the default first deposit. */
export const FIRST_DEPOSIT_PRESETS = ['0.5', '5', '20', '50'] as const
export const TOP_UP_PRESETS = ['5', '20', '50', '100'] as const
/**
 * The open request's deposit choices (RENT-RUNTIME-PLAN decision D4). It has
 * no price when it is signed, and $0.50 buys minutes at most prices, so the
 * smallest choice is $5 and it is the default.
 */
export const OPEN_REQUEST_DEPOSIT_PRESETS = ['5', '20', '50', '100'] as const

export type DepositCheck =
  | { ok: true; micro: string }
  | { ok: false; reason: 'empty' | 'invalid' | 'below-minimum' | 'above-balance'; message: string }

function dollars(micro: bigint): string {
  const whole = micro / 1_000_000n
  const cents = (micro % 1_000_000n) / 10_000n
  return `$${whole.toString()}.${cents.toString().padStart(2, '0')}`
}

/**
 * Read a typed dollar amount and say whether the chain will take it.
 *
 * `available`, when known, is what the wallet can put in, in `uddt`: its whole
 * balance, because the transaction carries no fee (the network's gas price is
 * zero, ADR-003). So "above balance" is caught here instead of after the
 * wallet has asked for a signature.
 */
export function checkDeposit(input: string, { minimum, available }: { minimum: bigint; available?: bigint | null | undefined }): DepositCheck {
  if (input.trim() === '') return { ok: false, reason: 'empty', message: 'Enter an amount in dollars.' }
  const micro = toMicroAmount(input)
  if (micro === null) return { ok: false, reason: 'invalid', message: 'Enter a positive dollar amount, such as 20 or 12.50.' }
  const value = BigInt(micro)
  if (value < minimum) {
    return { ok: false, reason: 'below-minimum', message: `The chain requires at least ${dollars(minimum)} and refuses a smaller deposit.` }
  }
  if (available !== undefined && available !== null && value > available) {
    return { ok: false, reason: 'above-balance', message: `Your wallet holds ${dollars(available < 0n ? 0n : available)}. Add funds from Billing, or deposit less.` }
  }
  return { ok: true, micro }
}

/**
 * Whole hours `micro` pays for at `rate` (per block), or null when the rate is
 * unknown or unreadable. Floor, not round: a runway shown as longer than it is
 * is the one direction that surprises a tenant with a closed lease.
 */
export function runwayHours(micro: string | bigint, rate: PriceCoin | null | undefined): number | null {
  const perHour = usdMicrosPerHour(rate, BLOCK_SECONDS)
  if (perHour === null || perHour <= 0n) return null
  return Number(BigInt(micro) / perHour)
}

/** "about 3 days", "about 14 hours", "under an hour". */
export function describeRunway(hours: number): string {
  if (hours < 1) return 'under an hour'
  if (hours < 48) return `about ${hours} hour${hours === 1 ? '' : 's'}`
  const days = Math.floor(hours / 24)
  return `about ${days} days`
}

/**
 * Whole minutes `micro` pays for at `rate` (per block), or null when either is
 * unreadable. Floored, as `runwayHours` is: never overstate a runway.
 *
 * Hours were too coarse on an expensive instance: at $70/hour a $20 top-up
 * buys 17 minutes, and "under an hour" does not tell a tenant whether to act
 * now or after lunch.
 */
export function runwayMinutes(micro: string | bigint, rate: PriceCoin | null | undefined): number | null {
  const perHour = usdMicrosPerHour(rate, BLOCK_SECONDS)
  if (perHour === null || perHour <= 0n) return null
  if (typeof micro === 'string' && !/^\d+$/.test(micro)) return null
  const value = BigInt(micro)
  if (value < 0n) return null
  return Number((value * 60n) / perHour)
}

/** "under a minute", "42 min", "6 h 55 min", "6 h", "3 days 4 h". */
export function describeRunwayPrecise(minutes: number): string {
  if (minutes < 1) return 'under a minute'
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) {
    const rest = minutes % 60
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
  }
  const days = Math.floor(hours / 24)
  const restHours = hours % 24
  return restHours === 0 ? `${days} days` : `${days} days ${restHours} h`
}

/**
 * The dollar amount, as the deposit field's input string, that buys `hours`
 * of runway at `rate`. Rounded UP to the cent, so the chosen duration is at
 * least what the tenant gets; null when the rate is unreadable.
 */
export function topUpForHours(rate: PriceCoin | null | undefined, hours: number): string | null {
  const perHour = usdMicrosPerHour(rate, BLOCK_SECONDS)
  if (perHour === null || perHour <= 0n || !Number.isInteger(hours) || hours <= 0) return null
  const micro = perHour * BigInt(hours)
  const cents = (micro + 9_999n) / 10_000n
  return `${(cents / 100n).toString()}.${(cents % 100n).toString().padStart(2, '0')}`
}
